import 'chartjs-adapter-date-fns'
import styles from './MonitorPerformanceChart.module.scss'
import { Bar } from 'react-chartjs-2'
import {
  BarElement,
  Chart as ChartJS,
  Legend,
  LineElement,
  LinearScale,
  PointElement,
  TimeScale,
  Title,
  Tooltip
} from 'chart.js'
import {
  durationInHours,
  formatDate,
  getCustomChartConfigs,
  getFormattedLocaleDate,
  hasDayChanged,
  roundToMinutesValue,
  secondsToHMSconversion,
  tickLabelManager
} from '../MonitorAvailabilityChart/functions'
import { generateChartData } from './processChartData'
import type { ChartOptions, ScriptableScaleContext } from 'chart.js'

ChartJS.register(
  TimeScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  BarElement
)

const DEFAULT_CALENDAR_WINDOW_IN_HOURS = 8

interface StepDuration {
  [key: string]: number[]
}

export interface Props {
  labels: string[]
  onClick: (runId: number) => void
  runIds: number[]
  stepsDurationList: StepDuration
  minDateTime: string
  maxDateTime: string
}

const MonitorPerformanceChart = ({
  labels,
  onClick,
  runIds,
  stepsDurationList,
  minDateTime,
  maxDateTime
}: Props) => {
  const dateDiffInHour = minDateTime && maxDateTime
    ? durationInHours(minDateTime, maxDateTime)
    : DEFAULT_CALENDAR_WINDOW_IN_HOURS

  const customChartConfigs: {
    barWidth: number
    stepSize: number
    ticksPadding: number
  } = getCustomChartConfigs(dateDiffInHour, labels.length)

  const chartOptions: ChartOptions<'bar'> = {
    responsive: true,
    maintainAspectRatio: false, // will srink to fit the data
    plugins: {
      legend: {
        display: true,
        position: 'bottom',
        align: 'start',
        labels: {
          boxHeight: 12,
          boxWidth: 12,
          padding: 16
        }
      },
      tooltip: {
        callbacks: {
          title (context: any) {
            return getFormattedLocaleDate(context[0].label)
          },
          label (context: any) {
            return ' ' + (context.dataset.label || 'Step-' + context.datasetIndex) + ': ' + +context.parsed.y.toFixed(2) + 's'
          },
          labelColor (context: any) {
            return {
              borderColor: context.dataset.backgroundColor,
              backgroundColor: context.dataset.backgroundColor,
              borderWidth: 2
            }
          },
          footer: tooltipItems => {
            const bar = formatDate(tooltipItems[0].label, 'yyyy-MM-DD HH:mm:ss')
            const barDataPosition = labels.indexOf(bar)
            const barSum = Object.keys(stepsDurationList).reduce((accumulator, data) => accumulator + +stepsDurationList[data][barDataPosition], 0)

            return secondsToHMSconversion(+barSum)
          }
        }
      }
    },
    scales: {
      // eslint-disable-next-line id-length
      x: {
        offset: false,
        stacked: true,
        min: roundToMinutesValue(minDateTime),
        max: roundToMinutesValue(maxDateTime, 'upper'),
        offsetAfterAutoskip: true,
        type: 'time',
        time: {
          minUnit: 'minute',
          unit: 'minute',
          tooltipFormat: 'HH:mm:ss yyyy-MM-dd',
          displayFormats: {
            minute: 'HH:mm'
          }
        },
        title: {
          text: 'Time of test',
          display: true
        },
        // Callback that runs after the scale fits to the canvas
        afterFit: chart => {
          chart.ticks = tickLabelManager(chart.ticks)
        },
        ticks: {
          source: 'auto',
          stepSize: customChartConfigs.stepSize, // [!] Time diff on the xAxis label ie. units between grid lines
          crossAlign: 'near', // Vertical distance from bottom of chart to tick text
          maxRotation: 0,
          minRotation: 0,
          align: 'center', // [default] hh:mm align ie. the text at th          autoSkip: true,
          autoSkipPadding: customChartConfigs.ticksPadding,
          maxTicksLimit: 32, // max x-axis label limit
          font: (context: ScriptableScaleContext) => {
            if (hasDayChanged(context.tick.label as string)) {
              return {
                weight: 'bold'
              }
            }

            return {}
          }
        },
        grid: {
          offset: true,
          drawOnChartArea: false,
          tickLength: 9,
          tickWidth: 2
        }
      },
      // eslint-disable-next-line id-length
      y: {
        stacked: true,
        title: {
          text: 'Duration (sec)',
          display: true
        },
        beginAtZero: true,
        border: {
          display: false
        },
        grid: {
          drawTicks: false,
          color: () => {
            const style = getComputedStyle(document.body)

            return style.getPropertyValue('--grid-color') || '#C0C0C0'
          }
        }
      }
    },
    onHover: (event: any, chartElement: any) => {
      // eslint-disable-next-line fp/no-mutation
      event.native.target.style.cursor = chartElement[0]
        ? 'pointer'
        : 'default'
    },
    onClick: (_event, element: any) => {
      if (element.length === 0) {
        return
      }

      if (typeof element[0]?.index !== 'undefined') {
        onClick(+runIds[element[0].index])
      }
    }
  }

  return (
    <div className={styles.chartContainerStyle}>
      <Bar
        data={
          generateChartData(
            labels,
            stepsDurationList,
            customChartConfigs.barWidth)
        }
        options={chartOptions}
      />
    </div>
  )
}

export default MonitorPerformanceChart
